import React from 'react';
import ReactDOM from 'react-dom/client';
import { DndContext, closestCenter, PointerSensor, KeyboardSensor } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import GwSelect from './GwSelect';
import ProductReorganizeItem from './ProductReorganizeItem';
import NoResult from './NoResult';

/** @typedef {import('../../../app/transformers/ProductTransformer').ReorganizeProductObj} ReorganizeProductObj */

/**
 * @typedef {object} ProductReorganizeProps
 * @property {string} csrf - Jeton CSRF pour les requêtes sécurisées
 * @property {string} categoryId - Identifiant de la catégorie pré-sélectionnée via l'URL (vide si aucune)
 */

/**
 * @typedef {object} ProductReorganizeState
 * @property {?number} categoryId - Identifiant de la catégorie en cours de réorganisation
 * @property {{[key: number]: string}} categoryNames - Noms des catégories (avec catégories parentes) indexés par identifiant
 * @property {Array<ReorganizeProductObj>} products - Produits de la catégorie triés par position
 * @property {boolean} loading - Si les produits sont en cours de chargement
 * @property {boolean} saving - Si un enregistrement est en cours
 * @property {string} error - Message d'erreur
 */

// NOTE descripteurs équivalents à useSensor(), les hooks n'étant pas utilisables dans un composant classe
const SENSORS = [
  { sensor: PointerSensor, options: {} },
  { sensor: KeyboardSensor, options: { coordinateGetter: sortableKeyboardCoordinates } }
];

class ProductReorganize extends React.Component {
  /**
   * @param {ProductReorganizeProps} props
   */
  constructor(props) {
    super(props);
    this.state = /** @type {ProductReorganizeState} */({
      categoryId: parseInt(props.categoryId, 10) || null,
      categoryNames: {},
      products: [],
      loading: false,
      saving: false,
      error: ''
    });
  }

  componentDidMount() {
    this.loadCategoryNames();
    this.loadProducts();
  }

  /**
   * Charge les noms des catégories pour l'affichage des catégories associées aux produits
   * @returns {Promise<void>}
   */
  async loadCategoryNames() {
    try {
      var response = await fetch('/api/_gw-select-product-category', { headers: { Authorization: 'gestiaweb' } });
      var result = await response.json();
      var categoryNames = (result.options || []).reduce((names, option) => {
        names[option.value] = option.name;
        return names;
      }, {});

      this.setState({ categoryNames });
    } catch (error) {
      this.setState({ error: 'Impossible de charger les catégories.' });
    }
  }

  /**
   * Lors de la sélection de la catégorie à réorganiser, recharge la page avec la catégorie dans l'URL
   * NOTE rechargement complet pour que l'URL de retour enregistrée côté serveur contienne la catégorie
   * @param {{value: string|number}} option - Option sélectionnée
   */
  selectCategory = option => {
    var url = new URL(window.location.href);

    url.searchParams.set('categoryId', String(parseInt(String(option.value), 10)));
    window.location.href = url.toString();
  };

  /**
   * Charge les produits de la catégorie sélectionnée
   * @returns {Promise<void>}
   */
  async loadProducts() {
    if (!this.state.categoryId) {
      return;
    }

    this.setState({ loading: true, error: '' });

    try {
      var response = await fetch(`/api/product/_reorganize?categoryId=${ this.state.categoryId }`, { headers: { Authorization: 'gestiaweb' } });
      var result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Impossible de charger les produits.');
      }

      this.setState({ products: result.item, loading: false });
    } catch (error) {
      this.setState({ loading: false, error: error instanceof Error ? error.message : 'Impossible de charger les produits.' });
    }
  }

  /**
   * Exécute une requête POST vers l'API
   * @param {string} url - URL de l'API
   * @param {any} data - Données à envoyer
   * @returns {Promise<any>} Résultat de l'API
   */
  async runRequest(url, data) {
    var response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: 'gestiaweb',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ...data, _csrf: this.props.csrf })
    });
    var result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Une erreur est survenue.');
    }

    return result.item;
  }

  /**
   * Déplace un produit dans la catégorie, les autres produits sont décalés, puis enregistre le nouvel ordre
   * @param {number} fromIndex - Index actuel du produit
   * @param {number} toIndex - Nouvel index du produit
   * @returns {Promise<void>}
   */
  moveProduct = async (fromIndex, toIndex) => {
    var products;

    if (this.state.saving || fromIndex === toIndex || toIndex < 0 || toIndex >= this.state.products.length) {
      return;
    }

    products = arrayMove(this.state.products, fromIndex, toIndex);
    this.setState({ products, saving: true, error: '' });

    try {
      products = await this.runRequest('/api/product/_reorganizepositions', {
        categoryId: this.state.categoryId,
        productIds: products.map(product => product.id)
      });
      this.setState({ products, saving: false });
    } catch (error) {
      this.setState({ saving: false, error: error instanceof Error ? error.message : 'Impossible d\'enregistrer les positions.' });
      await this.loadProducts();
    }
  };

  /**
   * Fin du drag and drop d'un produit
   * @param {import('@dnd-kit/core').DragEndEvent} event - Événement de fin de drag and drop
   */
  onDragEnd = event => {
    var { active, over } = event,
        fromIndex = this.state.products.findIndex(product => product.id === active.id),
        toIndex = over ? this.state.products.findIndex(product => product.id === over.id) : -1;

    if (toIndex !== -1) {
      this.moveProduct(fromIndex, toIndex);
    }
  };

  /**
   * Retire une catégorie d'un produit puis recharge les produits
   * @param {ReorganizeProductObj} product - Produit
   * @param {number} categoryId - Identifiant de la catégorie à retirer
   * @returns {Promise<void>}
   */
  removeCategory = async (product, categoryId) => {
    var categoryName = this.state.categoryNames[categoryId] || `#${ categoryId }`;

    if (this.state.saving || !window.confirm(`Retirer la catégorie ${ categoryName } du produit ${ product.name } ?`)) {
      return;
    }

    this.setState({ saving: true, error: '' });

    try {
      await this.runRequest('/api/product/_reorganizeremovecategory', { productId: product.id, categoryId });
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : 'Impossible de retirer la catégorie.' });
    }

    this.setState({ saving: false });
    await this.loadProducts();
  };

  render() {
    var { categoryId, categoryNames, products, loading, saving, error } = this.state;

    return (
      <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', height: '100%', gap: '8px' }}>
        <div className="uiblock uiblock--no-header" style={{ overflow: 'visible' }}>
          <div className="uiblock__content">
            <h4 className="title">Réorganiser les produits d'une catégorie</h4>
            <div className="space--8"></div>
            <p className="paragraph paragraph--no-margin">Recherchez une catégorie ou sous-catégorie puis déplacez les produits par glisser-déposer. Les autres produits de la catégorie sont décalés automatiquement.</p>
            <div className="space--16"></div>
            <GwSelect apiUrl="/api/_gw-select-product-category" inputPlaceholder="Rechercher une catégorie" required="true" inputOutputValue={categoryId ? String(categoryId) : ''} onSelect={this.selectCategory} />
            {error && (
              <>
                <div className="space--8"></div>
                <div className="mosaic__badge mosaic__badge--danger">{error}</div>
              </>
            )}
          </div>
        </div>
        {!categoryId && <NoResult mode="light" title="Aucune catégorie sélectionnée" message="Recherchez une catégorie pour afficher et réorganiser ses produits." />}
        {categoryId && loading && <p className="paragraph">Chargement des produits...</p>}
        {categoryId && !loading && !products.length && <NoResult mode="light" title="Aucun produit" message="Cette catégorie ne contient aucun produit." />}
        {categoryId && products.length > 0 && (
          <div className="table table--sticky" style={{ height: '100%', overflow: 'auto', minHeight: 0 }}>
            <table className="table__table">
              <thead>
                <tr className="table__tr">
                  <th className="table__th table__th-sticky"></th>
                  <th className="table__th table__th-sticky">Position</th>
                  <th className="table__th table__th-sticky">Image</th>
                  <th className="table__th table__th-sticky">Produit</th>
                  <th className="table__th table__th-sticky">État</th>
                  <th className="table__th table__th-sticky">
                    Catégories
                    <div className="table__th--description">Catégories associées au produit</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <DndContext sensors={SENSORS} collisionDetection={closestCenter} onDragEnd={this.onDragEnd}>
                  <SortableContext items={products.map(product => product.id)} strategy={verticalListSortingStrategy}>
                    {products.map((product, index) => (
                      <ProductReorganizeItem
                        key={product.id}
                        product={product}
                        index={index}
                        categoryNames={categoryNames}
                        disabled={saving}
                        onRemoveCategory={this.removeCategory}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}

var reorganizeProductNode = document.getElementById('reorganize-product');
if (reorganizeProductNode) {
  var root = ReactDOM.createRoot(reorganizeProductNode);
  root.render(<ProductReorganize csrf={reorganizeProductNode.dataset.csrf} categoryId={reorganizeProductNode.dataset.categoryId} />);
}
